import React from "react";

import { Grid } from "@material-ui/core";

import {
  Create,
  email,
  FormDataConsumer,
  maxLength,
  minLength,
  regex,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";
import setFieldData from "final-form-set-field-data";
import { useForm } from "react-final-form";

import { BackButton } from "../../../components/common/BackButton";
import { CarrierIdentifiersInput } from "../../../components/carrier/CarrierIdentifiersInput";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { REGEX_PHONE } from "../../../utils/constants";
import {
  validateCarrierFieldUnicity,
  validateCarrierIdentityMeta,
} from "./carrierUtils";
import { CarrierCreateHelp } from "./CarrierHelp";
import { ValidateCreateButton } from "./ValidateCreateButton";

// validateCarrierIdentityMeta comes last on each: the field's own rules go
// first, so a malformed email is called malformed rather than checked for
// duplicates it could never have.
const validateBusinessName = [required(), validateCarrierIdentityMeta];
const validateEmail = [required(), email(), validateCarrierIdentityMeta];
// Optional: a yard often books a carrier off an email thread and has no number
// for it yet. The shape rules below all skip an empty value, so a blank phone
// passes while a typed one is still held to 10-20 digits.
const validatePhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
  validateCarrierIdentityMeta,
];

const CarrierCreateForm = ({ notify, ...props }) => {
  const form = useForm();

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Grid container direction="column" spacing={1}>
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} lg={6}>
                  <TextInput
                    source="businessName"
                    label="Business Name"
                    variant="outlined"
                    fullWidth
                    validate={validateBusinessName}
                    onBlur={async () => {
                      await validateCarrierFieldUnicity("businessName", form);
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <TextInput
                    source="dbaName"
                    label="Doing business as"
                    helperText="Trading name, if different. Drivers can search it at the kiosk."
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <TextInput
                    source="email"
                    type="email"
                    label="Email"
                    variant="outlined"
                    fullWidth
                    parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                    validate={validateEmail}
                    onBlur={async () => {
                      await validateCarrierFieldUnicity("email", form);
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <TextInput
                    source="phone"
                    type="tel"
                    label="Phone"
                    variant="outlined"
                    fullWidth
                    validate={validatePhone}
                    onBlur={async () => {
                      await validateCarrierFieldUnicity("phone", form);
                    }}
                  />
                </Grid>
                {/* No blur check of its own — the numbers are checked on save,
                    where they outrank the three fields above. They do take part
                    in every blur above, though: a name collision the numbers
                    contradict is not reported, because the save will not treat
                    it as one either. */}
                <Grid item xs={12}>
                  <CarrierIdentifiersInput />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const CarrierCreateActions = (props) => (
  <TopToolbar>
    <HelpButton>
      <CarrierCreateHelp {...props} />
    </HelpButton>
    <BackButton {...props} />
  </TopToolbar>
);

const CarrierCreateToolbar = (props) => (
  <Toolbar {...props}>
    <ValidateCreateButton />
  </Toolbar>
);

export const CarrierCreate = (props) => {
  const notify = useNotify();

  return (
    <Create
      {...props}
      title={<ForceTitle title="New Carrier" />}
      actions={<CarrierCreateActions />}
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        mutators={{ setFieldData }}
        toolbar={<CarrierCreateToolbar />}
      >
        <CarrierCreateForm {...props} notify={notify} />
      </SimpleForm>
    </Create>
  );
};
